import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import clone from 'lodash/clone';
import { useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { Step } from 'src/common/interfaces/step.interface';
import { useEditorContext } from 'src/store/editor';

import { apiAddNodeToJourney, apiCreateStep, apiReorderStep, apiUpdateJourney } from 'src/utils/journeyApi';
import { EditorSection } from './section/index';
import { ContentSyncedStepsPanel } from './content/synced-steps-panel';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { CloseStrongIcon, SectionStrongIcon } from 'src/monet/icons';
import { removeNodeFromCurrentJourney } from './api';
import { StepEditorJourneyName } from './step-editor/journey-name';
import { IconButton } from 'src/common/icon-button';
import { useEditorNavigator } from './use-editor-navigator.hook';
import { ErrorInterface } from 'src/common/interfaces/error.interface';
import { useStepEditorContext } from 'src/store/step-editor';
import { Node } from 'src/common/interfaces/node.interface';
import { ContentAddNew } from './content/card-new';
import last from 'lodash/last';
import { Nullable } from 'src/types/nullable.type';
import { EventBus } from 'src/common/EventBus';
import { useAppContext } from 'src/onboarding/joyride-context';

// Keys for stored tour state from joyride-onboarding.tsx
const TOUR_CONTINUATION_KEY = 'journey_editor_tour_continuation';
const TOUR_STEP_INDEX_KEY = 'journey_editor_tour_step_index';
const TOUR_EDITOR_TYPE_KEY = 'journey_editor_tour_type';

export const EditorJourneyEdit = () => {
  const {
    journey,
    setJourney,
    currentActiveNodeId,
    setCurrentActiveNodeId,
    updateJourney,
    showSyncedSteps,
    currentStep,
    setShowSyncedSteps,
    setCurrentStep,
    resetEditor,
  } = useEditorContext();
  const { navigateToContentNew, navigateToJourneysList } = useEditorNavigator();
  const { setStateFromNode } = useStepEditorContext();
  const { hasSyncedSteps, nodeToAddAfter } = useEditorContext();
  const history = useHistory();
  const nodesLengthRef = useRef(0);
  const containerElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const { setState } = useAppContext();
  const tourResumeAttemptedRef = useRef(false);

  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const location = useLocation();

  // Check for stored tour state when component mounts
  useEffect(() => {
    // First check if we need to resume a tour
    const continuationFlag = localStorage.getItem(TOUR_CONTINUATION_KEY);
    
    if (continuationFlag === 'true' && !tourResumeAttemptedRef.current && journey) {
      // Mark that we've attempted to resume the tour to prevent duplicate attempts
      tourResumeAttemptedRef.current = true;
      
      // After a delay to ensure the DOM is fully loaded and stable
      const resumeTourTimeout = setTimeout(() => {
        // Signal to joyride-onboarding that we're ready to resume the tour
        console.log("Signaling tour ready to resume");
        EventBus.emit('onboarding:ready-to-resume');
      }, 1200); // Longer delay for better reliability

      return () => clearTimeout(resumeTourTimeout);
    }
  }, [journey]); // Depend on journey to ensure this runs after journey data is loaded

  useEffect(() => {
    const state: any = location.state;
    const successNotification = state?.successNotification;
    if (successNotification) {
      setSuccessNotification(successNotification);
      // clear location state so that it doesn't show up again on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const flattenNodes = journey.steps.map((step: Step) => step.nodes).flat();
    if (nodesLengthRef.current && flattenNodes.length > nodesLengthRef.current) {
      const lastNode = last(flattenNodes) as Node;
      const addedLastElement = document.querySelector(`div[data-node-id="${lastNode.id}"]`);
      if (addedLastElement) {
        addedLastElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
    nodesLengthRef.current = flattenNodes.length;
    if (!currentActiveNodeId) {
      const { state = {} } = location as { state: Record<string, unknown> };
      if (state.nodeId) {
        const activeNode = flattenNodes.find((node: Node) => node.id === state.nodeId);
        if (activeNode) {
          setStateFromNode(activeNode);
          setCurrentActiveNodeId(state.nodeId as number);
          window.history.replaceState({}, document.title);
        }
      } else {
        const stepsWithSections = journey.steps.filter((step: Step) => step.nodes.length > 0);
        if (stepsWithSections.length > 0) {
          const [initialSection] = stepsWithSections;
          const [initialNode] = initialSection.nodes;

          setCurrentActiveNodeId(initialNode.id);
          setStateFromNode(initialNode);
        }
      }
    }
  }, [history, journey.steps, currentActiveNodeId, location, resetEditor, setCurrentActiveNodeId, setStateFromNode]);

  const onDragNodeSuccess = (
    nodeUuid: any,
    sourceStepId: string,
    destinationStepId: string,
    sourceIdx: any,
    destinationIdx: number
  ) => {
    let stepsCopy = clone(journey.steps);

    let sourceStep = stepsCopy.find((sc: { id: number }) => sc.id === parseInt(sourceStepId));
    let destinationStep = stepsCopy.find((sc: { id: number }) => sc.id === parseInt(destinationStepId));

    // remove dragged node from source step nodes
    let [draggedNode] = sourceStep.nodes.splice(sourceIdx, 1);

    let destinationPreviousNode: any = null;

    // if destination idx is > 0, then find the previous node uuid
    if (destinationIdx > 0) {
      destinationPreviousNode = destinationStep.nodes[destinationIdx - 1];
    }

    destinationStep.nodes.splice(destinationIdx, 0, draggedNode);

    const stepsParams = {
      steps: stepsCopy,
    };

    updateJourney(stepsParams);

    const stepNodeParams = {
      name: draggedNode.name,
      is_email_required: draggedNode.is_email_required,
    };

    return removeNodeFromCurrentJourney(nodeUuid, sourceStepId).then(() => {
      return apiAddNodeToJourney(
        nodeUuid,
        destinationStepId,
        destinationPreviousNode ? destinationPreviousNode.uuid : null,
        stepNodeParams
      ).then((journey) => {
        setJourney(journey);
      });
    });
  };

  const lastStep: Step = useMemo(() => last(journey.steps)!, [journey.steps]);

  const onDragStepSuccess = (stepId: any, sourceIdx: number, destinationIdx: number) => {
    let stepsCopy = clone(journey.steps);

    let destinationPreviousStep = null;

    if (sourceIdx === destinationIdx) {
      return;
    }

    // if destination idx is > 0, then find the previous step id
    if (destinationIdx > 0) {
      // if last element
      if (sourceIdx < destinationIdx) {
        destinationPreviousStep = stepsCopy[destinationIdx];
      } else {
        destinationPreviousStep = stepsCopy[destinationIdx - 1];
      }
    }

    let [sourceStep] = stepsCopy.splice(sourceIdx, 1);
    stepsCopy.splice(destinationIdx, 0, sourceStep);

    const stepsParams = {
      steps: stepsCopy,
    };

    setJourney({ ...journey, ...stepsParams });

    return apiReorderStep(stepId, destinationPreviousStep ? destinationPreviousStep.id : null).then((journey) => {
      setJourney(journey);
    });
  };

  const onDragEnd = (result: DropResult) => {
    const { draggableId, source, destination, type } = result;

    if (!destination) {
      return;
    }

    const sourceIdx = source.index;
    const destinationIdx = destination.index;

    if (type === 'step' || type === 'DEFAULT') {
      const nodeUuid = draggableId;
      const sourceStepId = source.droppableId;
      const destinationStepId = destination.droppableId;

      onDragNodeSuccess(nodeUuid, sourceStepId, destinationStepId, sourceIdx, destinationIdx);
    } else if (type === 'journey') {
      const stepId = draggableId;
      onDragStepSuccess(stepId, sourceIdx, destinationIdx);
    }
  };

  const onAddSectionClick = useCallback(() => {
    const previousStep = journey.steps[journey.steps.length - 1] ? journey.steps[journey.steps.length - 1] : null;
    return apiCreateStep(journey.uuid, {
      ...(previousStep && { previous_step_id: previousStep.id }),
    }).then((step) => {
      setJourney({ ...journey, steps: [...journey.steps, step] });
      setSuccessNotification('Section successfully added.');
      const { current: containerElement } = containerElementRef;
      if (containerElement) {
        containerElement.scrollTo({
          top: containerElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
  }, [journey, setJourney, setSuccessNotification]);

  const onJourneyNameChanged = useCallback(
    async (value) => {
      if (!value) {
        setErrorNotification("Journey name can't be empty.");
        return;
      }
      if (value !== journey.name) {
        try {
          const updatedJourney = await apiUpdateJourney(journey.uuid, { name: value });
          setJourney(updatedJourney);
        } catch (error) {
          setErrorNotification((error as ErrorInterface).message);
        }
      }
    },
    [journey.name, journey.uuid, setErrorNotification, setJourney, setSuccessNotification]
  );

  const onCloseIconClicked = useCallback(() => {
    navigateToJourneysList();
  }, [navigateToJourneysList]);

  const onNewStepClicked = useCallback(async () => {
    EventBus.emit('editor:reset-stage', { resetEditor: true, showInitialStage: true });
    let stepToAdd = lastStep;
    console.log("stepToAdd", stepToAdd);
    if (!stepToAdd) {
      // this means there is no section present, so create a new one
      stepToAdd = await apiCreateStep(journey.uuid);
      setJourney({ ...journey, steps: [stepToAdd] });
    }
    navigateToContentNew(stepToAdd.id);
  }, [lastStep, navigateToContentNew, journey, setJourney]);

  return (
    <div className='absolute w-[368px] left-0 inset-y-0 z-20 transition-all overflow-hidden bg-bedrock-white space-y-lg pb-[96px] pt-lg px-md py-[62px]'>
      <Helmet>
        <title>Journey | Editing {journey.name}</title>
      </Helmet>
      <div className='flex flex-col space-y-lg'>
        <IconButton onClick={onCloseIconClicked}>
          <CloseStrongIcon />
        </IconButton>
        <StepEditorJourneyName onChange={onJourneyNameChanged} value={journey.name} />
        <div className='h-px bg-bedrock-gray-medium w-full rounded-[100px]' />
      </div>
      <div
        ref={containerElementRef}
        className='h-[calc(100vh-280px)] overflow-y-auto overflow-x-hidden scrollbar-hidden w-[370px] !mt-0'
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='journey' type='journey'>
            {(provided) => (
              <div ref={provided.innerRef}>
                <div className='flex flex-col space-y-lg mt-lg'>
                  {journey.steps.map((step: Step, index: number) => (
                    <Draggable key={step.id} draggableId={`${step.id}`} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={provided.draggableProps.style}
                          data-is-dragging={snapshot.isDragging}
                        >
                          <EditorSection
                            step={step}
                            isCollapsed={snapshot.isDragging}
                            activeNodeId={nodeToAddAfter?.id || currentActiveNodeId}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <div className='fixed left-0 bottom-0 p-md bg-bedrock-white z-40'>
        <div className='flex space-x-md'>
          <div className='flex items-center space-x-sm pointer-events-auto'>
            <ContentAddNew hasSyncedSteps={hasSyncedSteps} step={lastStep} onClick={onNewStepClicked} />
          </div>
          <div className='flex items-center space-x-sm pointer-events-auto'>
            <button
              type='button'
              className='btn-bedrock-secondary w-40 flex justify-center items-center space-x-xs pitch-onboarding-add-content'
              onClick={onAddSectionClick}
            >
              <SectionStrongIcon />
              <span>Add section</span>
            </button>
          </div>
        </div>
      </div>
      <ContentSyncedStepsPanel
        step={currentStep!}
        open={showSyncedSteps}
        onClose={() => {
          setShowSyncedSteps(false);
          setCurrentStep(currentStep);
        }}
      />
    </div>
  );
};
